import { History, Location } from 'history';
import mixpanel from 'mixpanel-browser';
import { FunctionalArea } from 'shared/models/analytics';
import routes from 'config/developerSite/routes';
import DevsiteEnv from 'modules/core/DevsiteEnv';
import { getOrCreateAnonymousDeviceId } from './anonymousDeviceId';
import { userMetricsEnabled } from './userMetricsPreference';
import wconsole from './wconsole';

export { FunctionalArea } from 'shared/models/analytics';

/** Property values Mixpanel's JS SDK typically accepts on events (primitives and primitive arrays only). */
type MixpanelPropertyValue = string | number | boolean | null | string[] | number[] | boolean[];
export type MixpanelProperties = Record<string, MixpanelPropertyValue>;

/**
 * See: https://matomo.org/docs/event-tracking/#how-to-set-up-matomo-event-tracking-with-javascript
 *
 * trackEvent(category, action, [name], [value]) -
 * Log an event with an event category (Videos, Music, Games...),
 * an event action (Play, Pause, Duration, Add Playlist, Downloaded, Clicked...),
 * and an optional event name and optional numeric value.
 *
 * @deprecated use FunctionalArea instead!
 * delete after MFE consumers have refactored to use `track` directly.
 */
export enum AnalyticsEventCategory {
  appCopy = 'App Copy',
  appDownload = 'App Download',
  appVersionDownload = 'App Version Download',
  appBuildDownload = 'App Build Download',
  appRawDownload = 'App Download - Raw',
  appDelete = 'App Delete',
  catalogDeploy = 'App Catalog Deploy',
  appDeploy = 'Deploy App',
  redirect = 'Redirect',
  general = 'General Site Action',
  recentAppsCard = 'Recent Apps Card',
  errorHandling = 'Error Handling',
  unexpectedError = 'Unexpected Error',
  homePage = 'Home Page',
  themeChanged = 'Theme Changed',
  appOverview = 'App Overview',
  consoleAnalytics = 'Console Analytics',
  appBuilder = 'App Builder',
  copilotChat = 'Copilot Chat',
  copilotSearch = 'Copilot Search',
  copilotBuilder = 'Copilot in App Builder',
}

/**
 * @deprecated
 */
export interface AnalyticsEvent {
  category: AnalyticsEventCategory;
  action: string;
}

/**
 * @deprecated
 * shorthand/factory for AnalyticsEvent
 */
const ae = (category: AnalyticsEventCategory, action: string): AnalyticsEvent => ({
  category,
  action,
});

/**
 * @deprecated
 * common actions for convenience
 */
enum AnalyticsEventAction {
  clicked = 'Clicked',
  start = 'Started',
  process = 'Processing',
  succeeded = 'Succeeded',
  failed = 'Failed',
}

/**
 * @deprecated
 */
const events = {
  appBuilderLogs: ae(AnalyticsEventCategory.appBuilder, 'App Build Logs Loaded'),
  appCopyStarted: ae(AnalyticsEventCategory.appCopy, AnalyticsEventAction.start),
  appCopying: ae(AnalyticsEventCategory.appCopy, AnalyticsEventAction.process),
  appCopySucceeded: ae(AnalyticsEventCategory.appCopy, AnalyticsEventAction.succeeded),
  appCopyFailed: ae(AnalyticsEventCategory.appCopy, AnalyticsEventAction.failed),
  appDownload: ae(AnalyticsEventCategory.appDownload, AnalyticsEventAction.start),
  appVersionDownload: ae(AnalyticsEventCategory.appVersionDownload, AnalyticsEventAction.start),
  appBuildDownload: ae(AnalyticsEventCategory.appBuildDownload, AnalyticsEventAction.start),
  appRawDownload: ae(AnalyticsEventCategory.appRawDownload, AnalyticsEventAction.start),
  appDeleteStarted: ae(AnalyticsEventCategory.appDelete, AnalyticsEventAction.start),
  appDeleteSucceeded: ae(AnalyticsEventCategory.appDelete, AnalyticsEventAction.succeeded),
  appDeleteFailed: ae(AnalyticsEventCategory.appDelete, AnalyticsEventAction.failed),
  catalogDeployStarted: ae(AnalyticsEventCategory.catalogDeploy, AnalyticsEventAction.start),
  catalogCopying: ae(AnalyticsEventCategory.catalogDeploy, AnalyticsEventAction.process),
  catalogOnlyCreated: ae(AnalyticsEventCategory.catalogDeploy, 'App Created, Not Deployed'),
  catalogAppDownload: ae(AnalyticsEventCategory.catalogDeploy, 'App Catalog Downloaded'),
  consoleAnalyticsLogsColumnsAdded: ae(AnalyticsEventCategory.consoleAnalytics, 'Logs Column Added'),
  consoleAnalyticsLogsDateRange: ae(AnalyticsEventCategory.consoleAnalytics, 'Logs Date Range Selected'),
  consoleAnalyticsLogsFilter: ae(AnalyticsEventCategory.consoleAnalytics, 'Logs Filter Added'),
  consoleAnalyticsLogsPageLoad: ae(AnalyticsEventCategory.consoleAnalytics, 'Logs Page Loaded'),
  consoleAnalyticsLogsPresets: ae(AnalyticsEventCategory.consoleAnalytics, 'Logs Preset Selected'),
  consoleAnalyticsLogsRefresh: ae(AnalyticsEventCategory.consoleAnalytics, 'Logs Refresh Clicked'),
  deployStarted: ae(AnalyticsEventCategory.appDeploy, AnalyticsEventAction.start),
  deploySucceeded: ae(AnalyticsEventCategory.appDeploy, AnalyticsEventAction.succeeded),
  deployFailed: ae(AnalyticsEventCategory.unexpectedError, 'Deploy Failed'),
  redirected: ae(AnalyticsEventCategory.redirect, 'Redirected'),
  fullscreen: ae(AnalyticsEventCategory.general, 'Went Fullscreen'),
  calloutCardCommunityForumAccessClick: ae(
    AnalyticsEventCategory.general,
    'Carousel: Community Forum Access Banner Link was clicked'
  ),
  calloutCardTutorialMigrationClick: ae(
    AnalyticsEventCategory.general,
    'Carousel: Tutorial Migration Link was clicked'
  ),
  recentCardLifecycle: ae(AnalyticsEventCategory.recentAppsCard, 'Manage Deployment selected'),
  recentCardActivity: ae(AnalyticsEventCategory.recentAppsCard, 'Activity selected'),
  recentCardMaltLogs: ae(AnalyticsEventCategory.recentAppsCard, 'MALT Logs selected'),
  recentCardBuildLog: ae(AnalyticsEventCategory.recentAppsCard, 'Build Log selected'),
  recentCardAppBuilder: ae(AnalyticsEventCategory.recentAppsCard, 'App Builder selected'),
  recentCardAppName: ae(AnalyticsEventCategory.recentAppsCard, 'App Name selected'),
  errorChunkReloaded: ae(AnalyticsEventCategory.errorHandling, 'Chunk Reloaded'),
  errorChunkFailed: ae(AnalyticsEventCategory.unexpectedError, 'Chunk Load Failed After Reload'),
  errorBoundaryTop: ae(AnalyticsEventCategory.unexpectedError, 'Top-Level Error Boundary'),
  errorAppBuilderBoundary: ae(AnalyticsEventCategory.unexpectedError, 'App Builder: Top-Level Error Boundary'),
  errorVisualModeBoundary: ae(AnalyticsEventCategory.unexpectedError, 'App Builder: Visual Mode Plugin Error Boundary'),
  errorRightPanelBoundary: ae(AnalyticsEventCategory.unexpectedError, 'App Builder: Right Panel Plugin Error Boundary'),
  homePageWhatsNewCardClick: ae(AnalyticsEventCategory.homePage, "What's New card CTA was clicked"),
  homePageNavCardCreateExtendAppClick: ae(AnalyticsEventCategory.homePage, 'Create Extend App NavCard click'),
  homePageNavCardCreateIntegrationAppClick: ae(AnalyticsEventCategory.homePage, 'Create Integration App NavCard click'),
  homePageRecentAppBuilderClick: ae(AnalyticsEventCategory.homePage, 'App Builder opened from shortcut icon'),
  homePageRecentRelatedAppBuilderClick: ae(
    AnalyticsEventCategory.homePage,
    'App Builder opened from related actions menu'
  ),
  homePageNavCardGraphQLExplorerClick: ae(AnalyticsEventCategory.homePage, 'GraphQL Explorer NavCard click'),
  homePageNavCardApiExplorerClick: ae(AnalyticsEventCategory.homePage, 'API Explorer NavCard click'),
  homePageNavCardSoapApiExplorerClick: ae(AnalyticsEventCategory.homePage, 'SOAP API Explorer NavCard click'),
  homePageNavCardManageAppsClick: ae(AnalyticsEventCategory.homePage, 'Manage Apps NavCard click'),
  homeMigrationGoToHomePageClick: ae(
    AnalyticsEventCategory.homePage,
    'User navigated to new home page from /console landing page'
  ),
  homeMigrationGoToAppsClick: ae(AnalyticsEventCategory.homePage, 'User navigated to Apps from /console landing page'),
  themeChangeDark: ae(AnalyticsEventCategory.themeChanged, 'dark'),
  themeChangeLight: ae(AnalyticsEventCategory.themeChanged, 'light'),
  overviewCopyOrchestrationUrl: ae(AnalyticsEventCategory.appOverview, 'Copy Orchestration URL'),
  overviewAppBuilderOpen: ae(AnalyticsEventCategory.appOverview, 'App Builder opened from Overview header'),
  overviewLogsOpen: ae(AnalyticsEventCategory.appOverview, 'App Overview Logs Loaded'),
};

const pathChars = '[^/]+';

/**
 * https://www.npmjs.com/package/history
 * @param location - the location object from history
 */
export const cleanLocation = (location: Location): Location => {
  // shallow copy so we don't affect the object that is used elsewhere in react router
  const cleanedLocation = { ...location };

  // we want to be able to identify /console/apps/view/APP1/manage and /console/apps/view/APP2/manage each as visits to the "deployment" section (from our macro analytics perspective, we don't care more about the page vs. which user app it is for)
  cleanedLocation.pathname = cleanedLocation.pathname.replace(
    new RegExp(`(${routes.consoleAppRoot}/)(${pathChars})`),
    '$1APP_REF_ID'
  );
  // same with activity
  cleanedLocation.pathname = cleanedLocation.pathname.replace(
    new RegExp(`(${routes.consoleAppActivity(pathChars)}/)(${pathChars})`),
    '$1VERSION_ID'
  );
  // same with builds
  cleanedLocation.pathname = cleanedLocation.pathname.replace(
    new RegExp(`(${routes.consoleAppBuildLog(pathChars, pathChars, '')})(${pathChars})`),
    '$1BUILD_ID'
  );

  if (cleanedLocation.pathname.startsWith(`${routes.appBuilder}${routes.buildLocalSuffix}`)) {
    // local app builder paths
    cleanedLocation.pathname = cleanedLocation.pathname.replace(
      new RegExp(`^${routes.buildLocalApp(pathChars)}`),
      routes.buildLocalApp('APP_REF_ID')
    );
    // local app builder component paths
    cleanedLocation.pathname = cleanedLocation.pathname.replace(
      new RegExp(`^${routes.buildLocalAppComponent(pathChars, '[^/.]+')}`),
      routes.buildLocalAppComponent('APP_REF_ID', 'COMPONENT_ID')
    );
  } else {
    // app builder paths
    cleanedLocation.pathname = cleanedLocation.pathname.replace(
      new RegExp(`^${routes.buildApp(pathChars)}`),
      routes.buildApp('APP_REF_ID')
    );
    // app builder component paths
    cleanedLocation.pathname = cleanedLocation.pathname.replace(
      new RegExp(`^${routes.buildAppComponent(pathChars, '[^/.]+')}`),
      routes.buildAppComponent('APP_REF_ID', 'COMPONENT_ID')
    );
  }

  // visiting a page like /somePage?page=3 should be recorded simply as a visit to /somePage
  if (cleanedLocation.pathname !== routes.search) cleanedLocation.search = '';

  wconsole.debug('Page location cleaned for metrics', cleanedLocation);

  return cleanedLocation;
};

const isMetricsTrackingEnabled = (): boolean => DevsiteEnv.ENABLE_ANALYTICS && userMetricsEnabled();

let mixpanelInitialized = false;
let mixpanelPeopleReady = false;

export const INITIAL_REFERRAL_CONTEXT_SESSION_STORAGE_KEY = 'devsite_initial_referral_context';
const LANDING_URL_PROPERTY = 'landing_url';

export interface InitialReferralContext extends MixpanelProperties {
  landingPath: string;
  [LANDING_URL_PROPERTY]: string;
  referrerHost: string | null;
}

/** Marketing attribution plus a few public content ids (YouTube `list` / `si`). */
const isAllowlistedMarketingKey = (key: string): boolean =>
  key === 'gclid' || key.startsWith('utm_') || key === 'list' || key === 'si';

export const collectMarketingQueryParams = (search: string): MixpanelProperties => {
  const marketing: MixpanelProperties = {};
  for (const [key, value] of new URLSearchParams(search)) {
    if (isAllowlistedMarketingKey(key)) {
      marketing[key] = value;
    }
  }
  return marketing;
};

/**
 * Rebuilds a URL from pathname + allowlisted marketing params only,
 * preventing sensitive query values from leaking to third-party analytics.
 */
export const sanitizeLandingUrl = (location: Pick<Location, 'pathname' | 'search'>): string => {
  const filtered = new URLSearchParams();
  for (const [key, value] of new URLSearchParams(location.search)) {
    if (isAllowlistedMarketingKey(key)) {
      filtered.set(key, value);
    }
  }
  const qs = filtered.toString();
  return qs ? `${location.pathname}?${qs}` : location.pathname;
};

/**
 * Same query allowlist as {@link sanitizeLandingUrl}, for an arbitrary CTA href
 * (relative or absolute). Keeps hash fragments for in-page anchors.
 */
export const sanitizeDestinationUrl = (href: string): string => {
  try {
    const isAbsolute = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(href);
    const url = isAbsolute ? new URL(href) : new URL(href, 'https://sanitize.invalid');
    const sanitized = `${sanitizeLandingUrl({ pathname: url.pathname, search: url.search })}${url.hash}`;
    return isAbsolute ? `${url.origin}${sanitized}` : sanitized;
  } catch {
    return href.split('?')[0] || href;
  }
};

const getReferrerHost = (referrer: string): string | null => {
  try {
    return new URL(referrer).hostname || null;
  } catch {
    return null;
  }
};

const buildInitialReferralContext = (context: unknown): InitialReferralContext | undefined => {
  if (typeof context !== 'object' || context === null) {
    return undefined;
  }

  if (
    !('landingPath' in context) ||
    typeof context.landingPath !== 'string' ||
    !context.landingPath.startsWith('/') ||
    context.landingPath.includes('?') ||
    context.landingPath.includes('#')
  ) {
    return undefined;
  }

  if (!('referrerHost' in context) || (context.referrerHost !== null && typeof context.referrerHost !== 'string')) {
    return undefined;
  }

  const referrerHost = context.referrerHost === null ? null : getReferrerHost(`https://${context.referrerHost}`);
  const marketing: MixpanelProperties = {};
  const marketingSearch = new URLSearchParams();
  Object.entries(context).forEach(([key, value]) => {
    if (isAllowlistedMarketingKey(key) && typeof value === 'string') {
      marketing[key] = value;
      marketingSearch.set(key, value);
    }
  });

  return {
    referrerHost,
    landingPath: context.landingPath,
    [LANDING_URL_PROPERTY]: sanitizeLandingUrl({ pathname: context.landingPath, search: marketingSearch.toString() }),
    ...marketing,
  };
};

const parseInitialReferralContext = (serializedContext: string | null): InitialReferralContext | undefined => {
  if (!serializedContext) {
    return undefined;
  }

  try {
    const context: unknown = JSON.parse(serializedContext);
    return buildInitialReferralContext(context);
  } catch {
    return undefined;
  }
};

/** Returns the factual referral context captured at the beginning of this browser session. */
export const getInitialReferralContext = (): InitialReferralContext | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    return parseInitialReferralContext(sessionStorage.getItem(INITIAL_REFERRAL_CONTEXT_SESSION_STORAGE_KEY));
  } catch {
    return undefined;
  }
};

/** Clears transient session state without losing attribution across an authentication redirect. */
export const clearSessionStoragePreservingInitialReferralContext = (): void => {
  const referralContext = getInitialReferralContext();

  try {
    sessionStorage.clear();
    if (referralContext) {
      sessionStorage.setItem(INITIAL_REFERRAL_CONTEXT_SESSION_STORAGE_KEY, JSON.stringify(referralContext));
    }
  } catch {
    // Storage-blocked browsers cannot retain referral context, but authentication must continue.
  }
};

/** Captures allowlisted initial referral facts once so later navigation cannot change attribution. */
export const captureInitialReferralContext = (
  location: Pick<Location, 'pathname' | 'search'> = window.location,
  referrer: string = document.referrer
): { context: InitialReferralContext; alreadyTracked: boolean } => {
  const existingContext = getInitialReferralContext();
  if (existingContext) {
    return { context: existingContext, alreadyTracked: true };
  }

  const context: InitialReferralContext = {
    referrerHost: getReferrerHost(referrer),
    landingPath: location.pathname,
    [LANDING_URL_PROPERTY]: sanitizeLandingUrl(location),
    ...collectMarketingQueryParams(location.search),
  };

  try {
    sessionStorage.setItem(INITIAL_REFERRAL_CONTEXT_SESSION_STORAGE_KEY, JSON.stringify(context));
  } catch {
    // Storage-blocked browsers still receive factual context on this page view.
  }

  return { context, alreadyTracked: false };
};

/**
 * Fires immediately after Mixpanel init so UTMs survive before React Router / cleaned page views run.
 * See: https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript
 */
const trackSessionFirstLandingPageView = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const { context, alreadyTracked } = captureInitialReferralContext();

  if (alreadyTracked) {
    return;
  }

  const eventName = `${FunctionalArea.GENERAL}: First Page View`;
  mixpanel.track(eventName, {
    functionalArea: FunctionalArea.GENERAL,
    action: 'First Page View',
    ...context,
  });
};

function initializeAnalytics() {
  if (mixpanelInitialized || !isMetricsTrackingEnabled()) {
    return;
  }

  const mixpanelProjectToken = DevsiteEnv.MIXPANEL_TOKEN;
  if (!mixpanelProjectToken) {
    return;
  }

  const anonymousId = getOrCreateAnonymousDeviceId();

  mixpanel.init(mixpanelProjectToken, {
    // Setting to true will write metrics payloads to console
    debug: DevsiteEnv.ANALYTICS_DEBUG,
    // eslint-disable-next-line camelcase -- mixpanel library uses snake_case
    api_host: DevsiteEnv.ANALYTICS_URL,
    // eslint-disable-next-line camelcase -- mixpanel library uses snake_case
    secure_cookie: DevsiteEnv.SECURE_COOKIE,
    // Initial campaign context is allowlisted and attached explicitly.
    // eslint-disable-next-line camelcase -- mixpanel library uses snake_case
    store_google: false,
    // save_referrer only controls Mixpanel's persisted initial referrer. Full $referrer
    // (including query strings) is still attached by the SDK unless blacklisted below.
    // eslint-disable-next-line camelcase -- mixpanel library uses snake_case
    save_referrer: false,
    // Disables Mixpanel cookies and SDK persistence. Returning-visitor identity is
    // our localStorage UUID passed to identify(), not Mixpanel persistence.
    // See https://github.com/mixpanel/mixpanel-js/blob/master/doc/readme.io/javascript-full-api-reference.md#mixpanelset_config
    // eslint-disable-next-line camelcase -- mixpanel library uses snake_case
    disable_persistence: true,
    // Strip Mixpanel's automatic raw URL and full referrer. Page views and custom events
    // send cleaned current_url instead; referral events attach referrerHost explicitly.
    // eslint-disable-next-line camelcase -- mixpanel library uses snake_case
    property_blacklist: ['$current_url', '$referrer'],
  });
  mixpanel.identify(anonymousId);
  mixpanelPeopleReady = true;
  mixpanelInitialized = true;
  trackSessionFirstLandingPageView();
}

/**
 * Every mixpanel call site is gated on this. It must report whether mixpanel actually
 * initialized, not just whether we wanted it to: `initializeAnalytics` bails without a
 * MIXPANEL_TOKEN, and calling into an uninitialized mixpanel throws on its missing config.
 */
const ensureAnalyticsReady = (): boolean => {
  if (!isMetricsTrackingEnabled()) {
    return false;
  }
  initializeAnalytics();
  return mixpanelInitialized;
};

initializeAnalytics();

/** Maps deprecated AnalyticsEventCategory to FunctionalArea for the new track format. */
const categoryToFunctionalArea: Record<AnalyticsEventCategory, FunctionalArea> = {
  [AnalyticsEventCategory.appCopy]: FunctionalArea.DOWNLOADS,
  [AnalyticsEventCategory.appDownload]: FunctionalArea.DOWNLOADS,
  [AnalyticsEventCategory.appVersionDownload]: FunctionalArea.DOWNLOADS,
  [AnalyticsEventCategory.appBuildDownload]: FunctionalArea.DOWNLOADS,
  [AnalyticsEventCategory.appRawDownload]: FunctionalArea.DOWNLOADS,
  [AnalyticsEventCategory.appDelete]: FunctionalArea.CONSOLE,
  [AnalyticsEventCategory.catalogDeploy]: FunctionalArea.APP_CATALOG,
  [AnalyticsEventCategory.appDeploy]: FunctionalArea.CONSOLE,
  [AnalyticsEventCategory.redirect]: FunctionalArea.GENERAL,
  [AnalyticsEventCategory.general]: FunctionalArea.GENERAL,
  [AnalyticsEventCategory.recentAppsCard]: FunctionalArea.CONSOLE,
  [AnalyticsEventCategory.errorHandling]: FunctionalArea.GENERAL,
  [AnalyticsEventCategory.unexpectedError]: FunctionalArea.GENERAL,
  [AnalyticsEventCategory.homePage]: FunctionalArea.HOME_PAGE,
  [AnalyticsEventCategory.themeChanged]: FunctionalArea.APP_BUILDER,
  [AnalyticsEventCategory.appOverview]: FunctionalArea.CONSOLE,
  [AnalyticsEventCategory.consoleAnalytics]: FunctionalArea.CONSOLE,
  [AnalyticsEventCategory.appBuilder]: FunctionalArea.APP_BUILDER,
  [AnalyticsEventCategory.copilotChat]: FunctionalArea.COPILOT,
  [AnalyticsEventCategory.copilotSearch]: FunctionalArea.COPILOT,
  [AnalyticsEventCategory.copilotBuilder]: FunctionalArea.COPILOT,
};

/**
 * Translates deprecated trackEvent calls to the new track format.
 * Used by legacyTracker for MFE backward compatibility.
 *
 * @param event            deprecated event with category and action
 * @param additionalName   ideally an ID uniquely identifying the object being acted upon - do NOT include any user data
 * @param additionalValue  uncommonly used, value reflecting something about the event
 * @deprecated use `track` instead!
 */
const trackEvent = (
  event: AnalyticsEvent,
  additionalName?: string,
  additionalValue?: number | string | Array<string>
) => {
  const functionalArea = categoryToFunctionalArea[event.category];
  if (!functionalArea) {
    wconsole.error(`Unknown analytics category: ${event.category}`);
    return;
  }
  const additionalProperties: MixpanelProperties = {};
  if (additionalName) additionalProperties.additionalName = additionalName;
  if (additionalValue !== undefined && additionalValue !== null) additionalProperties.additionalValue = additionalValue;
  track(functionalArea, event.action, additionalProperties);
};

/** Same cleaned URL props used by page views and custom events. */
const cleanedPageUrlProperties = (location: Location): MixpanelProperties => {
  const cleaned = cleanLocation(location);
  return {
    // eslint-disable-next-line camelcase -- analytics tracking uses snake_case
    current_url_path: cleaned.pathname,
    // eslint-disable-next-line camelcase -- analytics tracking uses snake_case
    current_url: `${DevsiteEnv.DEVSITE_DOMAIN}${cleaned.pathname}`,
  };
};

/**
 * Track an event in mixpanel. Do not send PII or user data in these events.
 *
 * @param functionalArea the functional area of the event, ex. "App Builder", "Copilot", "Console", etc.
 * @param action the action of the event, ex. "Succeeded"
 * @param additionalProperties additional properties to include with the event, ex. { builderMode: 'WRITE', versionId: 'v1.0.0', ... }
 * @example Analytics.track(Analytics.functionalArea.appBuilder, 'View', { builderMode: 'WRITE', versionId: 'v1.0.0' });
 * @example Analytics.track(Analytics.functionalArea.copilot, 'Show Chat', { location: 'App Builder Core' });
 */
const track = (functionalArea: FunctionalArea, action: string, additionalProperties?: MixpanelProperties): void => {
  const eventName = `${functionalArea}: ${action}`;
  const props = {
    ...additionalProperties,
    ...cleanedPageUrlProperties({
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      state: null,
    }),
    functionalArea,
    action,
  };

  if (ensureAnalyticsReady()) {
    wconsole.log('Mixpanel event track triggered', {
      eventName,
      props,
    });
    mixpanel.track(eventName, props);
  }
};

/** Records authentication completion with the referral context from the start of the browser session. */
export const trackSignInSucceeded = (): void => {
  track(FunctionalArea.GENERAL, 'Sign In Succeeded', getInitialReferralContext());
};

/**
 * Set a group for the current user in Mixpanel.
 * See: https://developer.mixpanel.com/docs/javascript#groups
 * @param groupKey The group key (e.g., "packages", etc.)
 * @param groupId The group identifier(s)
 */
const setGroup = (groupKey: string, groupId: string | string[] | number | number[]) => {
  if (ensureAnalyticsReady()) {
    wconsole.log('Mixpanel set group triggered', {
      groupKey,
      groupId,
    });
    mixpanel.set_group(groupKey, groupId);
  }
};

/**
 * Track a page view for Mixpanel.
 * See: https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#track-page-views
 * https://github.com/mixpanel/mixpanel-js/blob/v2.46.0/src/mixpanel-core.js#L980
 * Suggested usage: remove any id's in urls
 * @param pageView The group key (e.g., "current_url_path", "current_url", etc.)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
const trackPageView = (pageView: { [key: string]: any }) => {
  if (ensureAnalyticsReady()) {
    wconsole.log('Mixpanel track pageview triggered', pageView);
    mixpanel.track_pageview(pageView);
  }
};

/**
 * Tracks a page view for a location. Company and app ids are removed from the URL first, and nothing is
 * sent when metrics are disabled.
 */
const trackLocationPageView = (location: Location) => {
  trackPageView(cleanedPageUrlProperties(location));
};

/**
 * Tracks a page view on the initial page load and on every navigation of the given history.
 * https://www.npmjs.com/package/history
 * @deprecated Kept for MFE consumers. The app reports page views by calling `trackLocationPageView` when the
 * location changes.
 * @param history The history object to connect to.
 * @returns The history object with the listener applied.
 * */
const connectToHistory = (history: History) => {
  history.listen(trackLocationPageView);
  // history.listen only fires on subsequent navigations, not the initial page load
  trackLocationPageView(history.location);
  return history;
};

/**
 * @deprecated
 * delete after MFE consumers have refactored to use `track` directly.
 */
export interface LegacyTracker {
  trackEvent: (event: AnalyticsEvent, additionalName?: string, additionalValue?: number) => void;
}

/** Perserve a class-like interface for MFE consumers. Consumers could refactor to import the `trackEvent` method directly
 * @deprecated use `track` instead!
 * delete after MFE consumers have refactored to use `track` directly.
 */
const legacyTracker: LegacyTracker = {
  trackEvent,
};

/**
 * Mixpanel queues people.set/increment/remove operations until identify() has been called.
 * Init already identifies with the persistent anonymous device ID (never login PII).
 * This fallback only runs if a people API is used before that identify completed.
 */
const ensureMixpanelPeopleReady = (): void => {
  if (mixpanelPeopleReady) return;
  mixpanel.identify(mixpanel.get_distinct_id());
  mixpanelPeopleReady = true;
};

/**
 * Increment (or decrement with negative values) numerical properties on the current user's Mixpanel profile.
 * Useful for maintaining running totals like "Total Logins" or "Apps Created".
 * Do not include PII.
 * @param properties Map of property names to the amount to increment/decrement.
 */
const incrementProfileProperty = (properties: Record<string, number>) => {
  if (ensureAnalyticsReady()) {
    wconsole.log('Mixpanel people increment triggered', properties);
    ensureMixpanelPeopleReady();
    mixpanel.people.increment(properties);
  }
};

/**
 * Remove one or more values from a list property on the current user's Mixpanel profile.
 * Do not include PII.
 * @param listName The name of the list property.
 * @param value The value(s) to remove from the list.
 */
const removeFromProfileList = (listName: string, value: string | string[]) => {
  if (ensureAnalyticsReady()) {
    wconsole.log('Mixpanel people remove triggered', { listName, value });
    ensureMixpanelPeopleReady();
    mixpanel.people.remove({ [listName]: value });
  }
};

/**
 * Set (or batch-update) properties on the current user's Mixpanel profile.
 * Supports $set, $add, and $union semantics by passing the appropriate property values.
 * Do not include PII.
 * @param properties Map of profile property names to their new values.
 */
const setProfileProperties = (properties: MixpanelProperties) => {
  if (ensureAnalyticsReady()) {
    wconsole.log('Mixpanel people set triggered', properties);
    ensureMixpanelPeopleReady();
    mixpanel.people.set(properties);
  }
};

const getMFEAnalytics = (functionalArea: FunctionalArea) => ({
  connectToHistory,
  setGroup,
  track: (action: string, additionalProperties?: MixpanelProperties) =>
    track(functionalArea, action, additionalProperties),
  trackPageView,
});

export const Analytics = {
  getMFEAnalytics,
  connectToHistory,
  events,
  FunctionalArea,
  incrementProfileProperty,
  legacyTracker,
  removeFromProfileList,
  setGroup,
  setProfileProperties,
  track,
  trackEvent,
  trackLocationPageView,
  trackPageView,
};
